import React from 'react'
import Image from 'next/image'

import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog'
import { Button } from '@wartek-id/button'

import { useGetRelevanceTopic } from 'api/microlearning/useAPITopics'
import AvatarProfile from 'app/PelatihanMandiriPage/components/AvatarProfile'
import { Anchor } from 'components/Anchor'

import type { AksiNyataCreator } from 'types/microlearning'

type AksiNyataReviewer = {
  reviewerName: string
  reviewerUserID: string
}

export interface ViewHeadingProps {
  title: string
  author: AksiNyataCreator
  topikId: string
  topikName: string
  selectedTitle: string
  state?: string
  showCta?: boolean
  reviewer?: AksiNyataReviewer
}

const ReviewerInfos = [
  'Penelaah Aksi Nyata menelaah kesesuaian komponen Aksi Nyata dengan Panduan Aksi Nyata.',
  'Penelaah telah mengkuti Pelatihan Penelaahan Aksi Nyata Sejawat.',
  'Pembagian Aksi Nyata dalam penelaahan diacak oleh sistem.',
]

const DialogValidator = ({
  isOpen,
  onClose,
  id,
}: {
  isOpen: boolean
  onClose: () => void
  id?: string
}) => {
  const reviewerUrl = process.env.NEXT_PUBLIC_BASE_URL + `/profil/${id}`

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="sm">
      <DialogOverlay />
      <DialogContent>
        <div className="pt-8 pb-4 px-4">
          <div className="flex flex-col items-center">
            <Image
              src="https://storage.googleapis.com/microlearning-public-production/web-assets/img-aksi-nyata.png"
              alt="reviewer"
              width={128}
              height={128}
            />
          </div>
          <Text variant="heading-lg" className="text-center mt-4 mb-4">
            Penelaah Aksi Nyata
          </Text>
          <ul className="pl-6 list-disc px-2 mb-6">
            {ReviewerInfos.map((info, index) => (
              <li key={index}>
                <Text color="subdued">{info}</Text>
              </li>
            ))}
          </ul>
          <Button
            size="lg"
            className="mb-2"
            color="black"
            fullWidth
            onClick={onClose}
          >
            Tutup
          </Button>
          <Anchor href={reviewerUrl}>
            <Button size="lg" fullWidth color="white">
              Ke Profil Penelaah
            </Button>
          </Anchor>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const ViewHeading = ({
  title,
  author,
  selectedTitle,
  topikId,
  topikName,
  showCta = true,
  state,
  reviewer,
}: ViewHeadingProps) => {
  const isValidated = state === 'VALIDATED'
  const relevanceTopics = useGetRelevanceTopic(topikId)

  const [isOpenReviewer, setIsOpenReviewer] = React.useState(false)

  return (
    <div className="p-4">
      <div className={isValidated && 'p-4 border border-green-50 rounded-t-md'}>
        <Text className="mb-1" as="h2" variant="heading-lg">
          {title}
        </Text>
        <Text className="mb-4" variant="helper-bold" color="subdued">
          {topikName}&nbsp;&ndash;&nbsp;{selectedTitle}
        </Text>
        <AvatarProfile
          email={author.email}
          url={
            author.id &&
            process.env.NEXT_PUBLIC_BASE_URL + `/profil/${author.id}`
          }
          src={author.avatarUrl}
          name={author.name}
        />
        {showCta && (
          <div className="bg-surface-subdued border border-surface-neutral-subdued p-3 rounded">
            {relevanceTopics.isSuccess &&
            relevanceTopics.data.data.topics.length > 0 ? (
              <Text variant="body-sm" className="mb-3">
                Ingin bisa menerapkan pembelajaran seperti ini? Pelajari
                topik&nbsp;
                <strong>{relevanceTopics.data.data.topics[0].name}</strong>{' '}
                untuk dapat materi lengkapnya.
              </Text>
            ) : (
              <Text variant="body-sm" className="mb-3">
                Ingin bisa menerapkan pembelajaran seperti ini? Pelajari materi
                lengkapnya di Pelatihan Mandiri.
              </Text>
            )}
            <div>
              <Anchor
                href={`/${
                  process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                  'pelatihan-mandiri'
                }${
                  relevanceTopics.isSuccess &&
                  relevanceTopics.data.data.topics.length > 0
                    ? '/topik/' + relevanceTopics.data.data.topics[0].id
                    : ''
                }`}
              >
                <Text
                  className="inline-block py-[6px] px-[10px] border border-default rounded"
                  variant="body-sm"
                >
                  <strong>
                    {relevanceTopics.isSuccess &&
                    relevanceTopics.data.data.topics.length > 0
                      ? `Buka ${relevanceTopics.data.data.topics[0].name}`
                      : 'Lihat Topik Pelatihan'}
                  </strong>
                </Text>
              </Anchor>
            </div>
          </div>
        )}
      </div>
      {isValidated && (
        <>
          <div
            className="flex items-center bg-green-50 p-2 rounded-b-md text-white cursor-pointer justify-between"
            onClick={() => setIsOpenReviewer(!!reviewer.reviewerName)}
          >
            <div className="flex items-center">
              <Icon
                fontSize="small"
                className="mr-2 text-white"
                color="inverse"
              >
                check_circle
              </Icon>
              {!reviewer.reviewerName ? (
                <div className="text-sm font-semibold">Sudah ditelaah</div>
              ) : (
                <div className="text-sm font-semibold">
                  Ditelaah oleh {reviewer.reviewerName}
                </div>
              )}
            </div>
            {reviewer.reviewerName && (
              <Icon fontSize="small" color="inverse">
                chevron_right
              </Icon>
            )}
          </div>
          <DialogValidator
            isOpen={isOpenReviewer}
            onClose={() => setIsOpenReviewer(false)}
            id={reviewer.reviewerUserID}
          />
        </>
      )}
    </div>
  )
}

export default ViewHeading
